/**
 * Email Communications - Type Definitions
 * Types specific to email communication management
 */

import type { AppEmailModelView } from '@actschurch/ccms-sdk';
import type { AppEmailTemplateModelView } from '@actschurch/ccms-sdk';
import type { CommunicationStatus } from '../types';

// ============================================================================
// SDK Model Interfaces (imported from SDK)
// ============================================================================

export type { AppEmailModelView, AppEmailTemplateModelView };

/** A reference to an uploaded attachment stored in FileManagement/S3. */
export interface EmailAttachmentRef {
  fileId: string;
  fileName?: string;
  contentType?: string;
  size?: number;
}

// ============================================================================
// Extended Email Model (matching actual API response)
// ============================================================================

export interface EmailApiResponse {
  emailID: number;
  recipient: string;
  cc: string;
  bcc: string;
  replyTo?: string | null;
  subject: string;
  body: string;
  isHtml: boolean;
  from: string;
  isSent: boolean;
  status?: string;
  sentDate: string | null;
  errorMessage: string | null;
  externalMessageId?: string | null;
  attachments?: EmailAttachmentRef[];
  // SES delivery tracking
  deliveredDateTime?: string | null;
  openedDateTime?: string | null;
  clickedDateTime?: string | null;
  readDateTime?: string | null;
  bounceType?: string | null;
  unsubscribeDateTime?: string | null;
  trackingStatus?: string;
  trackingStatusColor?: string;
  templateID: number | null;
  templateName: string | null;
  templateDescription: string | null;
  priority: number;
  priorityName: string;
  priorityColor: string | null;
  scheduledFor: string | null;
  correlationId: string | null;
  createdDateTime: number;
  modifiedDateTime: number;
  isScheduled: boolean;
  hasError: boolean;
  isPending: boolean;
  emailAge: number;
  totalRecipients: number;
  deliveryStatus: string;
  deliveryStatusColor: string;
  priorityLevel: string;
  effectivePriorityColor: string;
  displayName: string;
  timeDisplay: string;
  bodyPreview: string;
  searchKeywords: string;
}

// ============================================================================
// Email Queue Item (with required ID for DataTable)
// ============================================================================

export interface EmailQueueRow {
  id: number;
  emailID: number;
  recipient: string;
  cc: string;
  bcc: string;
  replyTo?: string | null;
  subject: string;
  body: string;
  isHtml: boolean;
  from: string;
  isSent: boolean;
  status?: string;
  sentDate: string | null;
  errorMessage: string | null;
  externalMessageId?: string | null;
  attachments?: EmailAttachmentRef[];
  deliveredDateTime?: string | null;
  openedDateTime?: string | null;
  clickedDateTime?: string | null;
  readDateTime?: string | null;
  bounceType?: string | null;
  unsubscribeDateTime?: string | null;
  trackingStatus?: string;
  templateID: number | null;
  templateName: string | null;
  priority: number;
  priorityName: string;
  scheduledFor: string | null;
  createdDateTime: number;
  modifiedDateTime: number;
  isScheduled: boolean;
  hasError: boolean;
  isPending: boolean;
  deliveryStatus: string;
  deliveryStatusColor: string;
  timeDisplay: string;
  displayName: string;
}

export type EmailTemplateRow = AppEmailTemplateModelView & {
  id: number; // Map template ID to id for DataTable compatibility
};

// ============================================================================
// Component Props Interfaces
// ============================================================================

export interface EmailQueueTabProps {
  queueItems: EmailApiResponse[];
  loading?: boolean;
  onRefresh: () => void;
  onView: (item: EmailApiResponse) => void;
  onEdit: (item: EmailApiResponse) => void;
  onRetry: (id: number) => Promise<void>;
  onUpdatePriority: (id: number, priority: number) => Promise<void>;
}

export interface EmailQueueModalProps {
  opened: boolean;
  onClose: () => void;
  item: EmailApiResponse | null;
  mode: 'view' | 'edit';
  onSave?: (data: Partial<EmailApiResponse>) => Promise<void>;
  loading?: boolean;
}

export interface EmailTemplatesTabProps {
  templates: AppEmailTemplateModelView[];
  loading?: boolean;
  onRefresh: () => void;
  onView: (template: AppEmailTemplateModelView) => void;
  onEdit: (template: AppEmailTemplateModelView) => void;
  onDelete: (id: number) => Promise<void>;
  onCreate: () => void;
}

export interface EmailTestTabProps {
  templates: AppEmailTemplateModelView[];
  loading?: boolean;
  onSend: (data: EmailTestData) => Promise<void>;
}

export interface EmailTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  template: AppEmailTemplateModelView | null;
  mode: 'view' | 'edit' | 'create';
  onSave?: (data: Partial<AppEmailTemplateModelView>) => Promise<void>;
  loading?: boolean;
}

// ============================================================================
// Form Data Interfaces
// ============================================================================

export interface EmailTestData {
  useTemplate: boolean;
  templateId?: number;
  recipient: string;
  ccEmail?: string;
  bccEmail?: string;
  replyTo?: string;
  fromName?: string;
  subject: string;
  body: string;
  isHTML: boolean;
  priority: number;
  scheduledDateTime?: Date;
  attachments?: EmailAttachmentRef[];
  /** JSON of template variable values (template mode), forwarded to the email Payload. */
  payload?: string;
  addToQueue: boolean; // true = queue, false = send now
}

// ============================================================================
// Helper Functions
// ============================================================================

export function mapEmailToRow(email: EmailApiResponse): EmailQueueRow {
  return {
    id: email.emailID,
    emailID: email.emailID,
    recipient: email.recipient,
    cc: email.cc,
    bcc: email.bcc,
    replyTo: email.replyTo,
    subject: email.subject,
    body: email.body,
    isHtml: email.isHtml,
    from: email.from,
    isSent: email.isSent,
    status: email.status,
    sentDate: email.sentDate,
    errorMessage: email.errorMessage,
    externalMessageId: email.externalMessageId,
    attachments: email.attachments,
    deliveredDateTime: email.deliveredDateTime,
    openedDateTime: email.openedDateTime,
    clickedDateTime: email.clickedDateTime,
    readDateTime: email.readDateTime,
    bounceType: email.bounceType,
    unsubscribeDateTime: email.unsubscribeDateTime,
    trackingStatus: email.trackingStatus,
    templateID: email.templateID,
    templateName: email.templateName,
    priority: email.priority,
    priorityName: email.priorityName,
    scheduledFor: email.scheduledFor,
    createdDateTime: email.createdDateTime,
    modifiedDateTime: email.modifiedDateTime,
    isScheduled: email.isScheduled,
    hasError: email.hasError,
    isPending: email.isPending,
    deliveryStatus: email.deliveryStatus,
    deliveryStatusColor: email.deliveryStatusColor,
    timeDisplay: email.timeDisplay,
    displayName: email.displayName,
  };
}

export function mapTemplateToRow(template: AppEmailTemplateModelView): EmailTemplateRow {
  return {
    ...template,
    id: template.templateID,
  };
}

// ============================================================================
// Status Helper Functions
// ============================================================================

export function getEmailStatusColor(email: EmailApiResponse): string {
  if (email.hasError) return 'red';
  if (email.isPending) return 'yellow';
  if (email.isSent) return 'green';
  if (email.isScheduled) return 'blue';
  return 'gray';
}

export function getEmailStatusLabel(email: EmailApiResponse): string {
  return email.deliveryStatus || 'Unknown';
}
